import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import LicenseContext from 'src/contexts/LicenseContext';
import { Senario } from 'src/entities/planningEntity';
import { HowToPlanning, PlanningConditions } from 'src/entities/PlanningHistory.entity';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import { buildPlanningConditionsSnapshot, planningPriorityToPriorityMode } from './buildPlanningConditionsSnapshot';

type DispatchModel = 'opus' | 'repro' | 'fusion';

type Props = {
  screenName: string;
  updateBalancedLoading: (balancedLoading: boolean) => void;
  updateExcludeToll: (bool: boolean) => void;
  updateIncludeFutureDeliveries: (bool: boolean) => void;
  updateAutoSplitOrders: (bool: boolean) => void;
  updateDontExchangePreroute: (bool: boolean) => void;
  updateConcurrentAllOrNothing: (bool: boolean) => void;
  updateExpandWorkingStartMinutes: (minutes: number) => void;
  updateExpandWorkingEndMinutes: (minutes: number) => void;
  updateExpandLoadStartMinutes: (minutes: number) => void;
  updateExpandLoadEndMinutes: (minutes: number) => void;
  updateExpandUnloadStartMinutes: (minutes: number) => void;
  updateExpandUnloadEndMinutes: (minutes: number) => void;
  updateExpandMaxVolumeRate: (rate: number) => void;
  updateExpandMaxLoadCapacityRate: (rate: number) => void;
  planningRequest: (shiftIds: number[], orderIds: number[]) => void;
  onClose: () => void;
  selectedDeliveryIds: number[];
  selectedOrderIds: number[];
  startOn: string;
  relaxedRules: RelaxedRuleVo[];
  selectedRelaxedRules: RelaxedRuleVo[];
  setSelectedRelaxedRules: (value: RelaxedRuleVo[]) => void;
  setPriorityLargeTrucks: (priority: number) => void;
  priorityLargeTrucks: number;
  updateActivateIncrementalMaxNumPistons: (bool: boolean) => void;
  updateSenario: (senario: Senario) => void;
  updateDisableGlobalOpt: (bool: boolean) => void;
  updateTraceCompanyId: (id: number) => void;
  /**
   * 選択済みのうち、呼び出し元のフィルタで非表示になっている件数。
   * 未指定なら警告を出さない（V3Plans / V3Timeline / TrunkTransportation は未指定のまま）。
   */
  hiddenSelectedOrderCount?: number;
  /**
   * 画面設定 (buildPlanningConditionsSnapshot の返り値) が変わるたびに呼ばれる。
   * シナリオ履歴 (planning_setting.planningConditions) を必要とするのは Map だけなので
   * optional とし、localStorage への保存は従来どおり本コンポーネント自身が行う。
   */
  onPlanningConditionsChange?: (conditions: PlanningConditions) => void;
};

// Why not value リネーム: value (opus/repro/fusion) を変えると saved.dispatchModel / localStorage
//   互換が壊れるため据え置き、label / description のみ #286 の新仕様へ差し替える。
const dispatchModelDefs: { value: DispatchModel; label: string; description: string }[] = [
  { value: 'opus', label: 'LOG-3.0', description: '標準の配車モデル' },
  { value: 'repro', label: 'LOG-3.0 Trace', description: '過去の配車傾向を再現' },
  { value: 'fusion', label: 'LOG-3.0 Opus', description: '過去の配車傾向を基に最適化' },
];

// 学習配車 (senario: apprentice) を使わない標準モデル。
// 注意: value 'opus' はラベル「LOG-3.0」= 標準 (setcover) を指す。
//       学習配車の「LOG-3.0 Opus」は value 'fusion' の方であり、名前が交差している。
const STANDARD_DISPATCH_MODEL: DispatchModel = 'opus';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 4,
          height: 16,
          borderRadius: 1,
          bgcolor: 'primary.main',
          flexShrink: 0,
        }}
      />
      <Typography variant="subtitle2" fontWeight="bold">
        {children}
      </Typography>
    </Box>
  );
}

type ConstraintRowProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
  unit: string;
  max?: number;
  disabled?: boolean;
};

function ConstraintRow({ label, value, onChange, unit, max = 120, disabled = false }: ConstraintRowProps) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: '4rem', flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Slider
        size="small"
        value={value}
        onChange={(_, v) => { if (typeof v === 'number') onChange(v); }}
        step={5}
        min={0}
        max={max}
        disabled={disabled}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        type="number"
        inputProps={{ min: 0, max, step: 1 }}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const n = Math.min(max, Math.max(0, Number(e.target.value) || 0));
          onChange(n);
        }}
        sx={{ width: '5rem' }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: '2.5rem', flexShrink: 0 }}>
        {unit}
      </Typography>
    </Stack>
  );
}

type ToggleSwitchRowProps = {
  label: string;
  tooltip?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

function ToggleSwitchRow({ label, tooltip, checked, onChange }: ToggleSwitchRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 0.75,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </Tooltip>
        )}
      </Box>
      <Switch
        size="small"
        checked={checked}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      />
    </Box>
  );
}

const PlanningPresenter: FC<Props> = memo(
  ({
    screenName,
    updateBalancedLoading,
    updateExcludeToll,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    planningRequest,
    onClose,
    selectedDeliveryIds,
    selectedOrderIds,
    relaxedRules,
    selectedRelaxedRules,
    setSelectedRelaxedRules,
    setPriorityLargeTrucks,
    priorityLargeTrucks,
    updateActivateIncrementalMaxNumPistons,
    updateSenario,
    updateDisableGlobalOpt,
    updateTraceCompanyId,
    hiddenSelectedOrderCount,
    onPlanningConditionsChange,
  }) => {
    const licenseContext = useContext(LicenseContext);

    const [howToPlanning, setHowToPlanning] = useState<HowToPlanning>('最適化');
    const [priorityMode, setPriorityMode] = useState<'none' | 'nearby' | 'far'>('none');
    const [dispatchModel, setDispatchModel] = useState<DispatchModel>('opus');
    type VehicleSizePreference = 'none' | 'small' | 'large';
    const [vehicleSizePreference, setVehicleSizePreference] = useState<VehicleSizePreference>('none');
    const [availabilityOfExpressway, setAvailabilityOfExpressway] = useState<boolean>(false);
    const [includeFutureDeliveries, setIncludeFutureDeliveries] = useState<boolean>(false);
    const [autoSplitOrders, setAutoSplitOrders] = useState<boolean>(false);
    const [dontExchangePreroute, setDontExchangePreroute] = useState<boolean>(true);
    const [concurrentAllOrNothing, setConcurrentAllOrNothing] = useState<boolean>(true);
    const [activateIncrementalMaxNumPistons, setActivateIncrementalMaxNumPistons] = useState<boolean>(false);
    const [expandWorkingTimeStartMinutes, setExpandWorkingTimeStartMinutes] = useState<number>(0);
    const [expandWorkingTimeEndMinutes, setExpandWorkingTimeEndMinutes] = useState<number>(0);
    const [expandLoadStartMinutes, setExpandLoadStartMinutes] = useState<number>(0);
    const [expandLoadEndMinutes, setExpandLoadEndMinutes] = useState<number>(0);
    const [expandUnloadStartMinutes, setExpandUnloadStartMinutes] = useState<number>(0);
    const [expandUnloadEndMinutes, setExpandUnloadEndMinutes] = useState<number>(0);
    const [expandMaxVolumeRate, setExpandMaxVolumeRate] = useState<number>(0);
    const [expandMaxLoadCapacityRate, setExpandMaxLoadCapacityRate] = useState<number>(0);
    const [isExecuting, setIsExecuting] = useState<boolean>(false);
    const [constraintExpanded, setConstraintExpanded] = useState<boolean>(false);

    const useAiPlanning = licenseContext.config?.use_ai_planning === true;

    // 制約セクションに値が設定されている場合は自動で開く（閉じるのはユーザー操作のみ）
    const hasConstraintValues = useMemo(
      () =>
        selectedRelaxedRules.length > 0 ||
        expandWorkingTimeStartMinutes > 0 ||
        expandWorkingTimeEndMinutes > 0 ||
        expandLoadStartMinutes > 0 ||
        expandLoadEndMinutes > 0 ||
        expandUnloadStartMinutes > 0 ||
        expandUnloadEndMinutes > 0 ||
        expandMaxLoadCapacityRate > 0 ||
        expandMaxVolumeRate > 0,
      [
        selectedRelaxedRules,
        expandWorkingTimeStartMinutes,
        expandWorkingTimeEndMinutes,
        expandLoadStartMinutes,
        expandLoadEndMinutes,
        expandUnloadStartMinutes,
        expandUnloadEndMinutes,
        expandMaxLoadCapacityRate,
        expandMaxVolumeRate,
      ],
    );

    useEffect(() => {
      if (hasConstraintValues) setConstraintExpanded(true);
    }, [hasConstraintValues]);

    // How: dispatchModel を senario / disable_global_opt / trace_company_id へマッピングする(#286)
    //      AI配車ライセンスが無い間はモデル選択 UI を描画しないため、dispatchModel の値に
    //      関わらず標準モデル (setcover) として送出する。
    // Why not: localStorage 復元 (setDispatchModel) 側をガードしない。localStorage は origin 単位で
    //          保持され、config 取得前の初回レンダーでは useAiPlanning が false になる。復元を止めると
    //          その直後に走る保存 useEffect が dispatchModel: 'opus' で上書きし、ライセンスを持つ
    //          ユーザーの選択まで消えてしまう。送出の入口だけを塞ぐ。
    useEffect(() => {
      const companyId = licenseContext.config?.selected_company_id ?? 0;
      const effectiveModel: DispatchModel = useAiPlanning ? dispatchModel : STANDARD_DISPATCH_MODEL;
      if (effectiveModel === 'repro') {
        // LOG-3.0 Trace: apprentice + g-opt無効
        updateSenario('apprentice');
        updateTraceCompanyId(companyId);
        updateDisableGlobalOpt(true);
      } else if (effectiveModel === 'fusion') {
        // LOG-3.0 Opus: apprentice + g-opt有効
        updateSenario('apprentice');
        updateTraceCompanyId(companyId);
        updateDisableGlobalOpt(false);
      } else {
        // LOG-3.0 (opus): 標準 setcover
        updateSenario('setcover');
        updateTraceCompanyId(0);
        updateDisableGlobalOpt(false);
      }
    }, [dispatchModel, useAiPlanning, licenseContext.config?.selected_company_id, updateSenario, updateTraceCompanyId, updateDisableGlobalOpt]);

    useEffect(() => {
      updateBalancedLoading(howToPlanning === '平準化');
    }, [howToPlanning, updateBalancedLoading]);

    // vehicleSizePreference → priorityLargeTrucks マッピング
    useEffect(() => {
      if (vehicleSizePreference === 'none') setPriorityLargeTrucks(0.5);
      else if (vehicleSizePreference === 'small') setPriorityLargeTrucks(0);
      else setPriorityLargeTrucks(1.0);
    }, [vehicleSizePreference, setPriorityLargeTrucks]);

    useEffect(() => {
      updateExcludeToll(!availabilityOfExpressway);
    }, [availabilityOfExpressway, updateExcludeToll]);

    useEffect(() => {
      updateIncludeFutureDeliveries(includeFutureDeliveries);
    }, [includeFutureDeliveries, updateIncludeFutureDeliveries]);

    useEffect(() => {
      updateAutoSplitOrders(autoSplitOrders);
    }, [autoSplitOrders, updateAutoSplitOrders]);

    useEffect(() => {
      updateDontExchangePreroute(dontExchangePreroute);
    }, [dontExchangePreroute, updateDontExchangePreroute]);

    useEffect(() => {
      updateConcurrentAllOrNothing(concurrentAllOrNothing);
    }, [concurrentAllOrNothing, updateConcurrentAllOrNothing]);

    useEffect(() => {
      updateActivateIncrementalMaxNumPistons(activateIncrementalMaxNumPistons);
    }, [activateIncrementalMaxNumPistons, updateActivateIncrementalMaxNumPistons]);

    useEffect(() => {
      updateExpandWorkingStartMinutes(expandWorkingTimeStartMinutes);
    }, [expandWorkingTimeStartMinutes, updateExpandWorkingStartMinutes]);

    useEffect(() => {
      updateExpandWorkingEndMinutes(expandWorkingTimeEndMinutes);
    }, [expandWorkingTimeEndMinutes, updateExpandWorkingEndMinutes]);

    useEffect(() => {
      updateExpandLoadStartMinutes(expandLoadStartMinutes);
    }, [expandLoadStartMinutes, updateExpandLoadStartMinutes]);

    useEffect(() => {
      updateExpandLoadEndMinutes(expandLoadEndMinutes);
    }, [expandLoadEndMinutes, updateExpandLoadEndMinutes]);

    useEffect(() => {
      updateExpandUnloadStartMinutes(expandUnloadStartMinutes);
    }, [expandUnloadStartMinutes, updateExpandUnloadStartMinutes]);

    useEffect(() => {
      updateExpandUnloadEndMinutes(expandUnloadEndMinutes);
    }, [expandUnloadEndMinutes, updateExpandUnloadEndMinutes]);

    useEffect(() => {
      updateExpandMaxVolumeRate(expandMaxVolumeRate);
    }, [expandMaxVolumeRate, updateExpandMaxVolumeRate]);

    useEffect(() => {
      updateExpandMaxLoadCapacityRate(expandMaxLoadCapacityRate);
    }, [expandMaxLoadCapacityRate, updateExpandMaxLoadCapacityRate]);

    // 制約選択時に緩和値をリセット
    useEffect(() => {
      if (!selectedRelaxedRules?.length) return;
      if (selectedRelaxedRules.includes('労働時間')) {
        setExpandWorkingTimeStartMinutes(0);
        setExpandWorkingTimeEndMinutes(0);
      }
      if (selectedRelaxedRules.includes('指定時間')) {
        setExpandLoadStartMinutes(0);
        setExpandLoadEndMinutes(0);
        setExpandUnloadStartMinutes(0);
        setExpandUnloadEndMinutes(0);
      }
      if (selectedRelaxedRules.includes('重量')) {
        setExpandMaxLoadCapacityRate(0);
      }
      if (selectedRelaxedRules.includes('体積')) {
        setExpandMaxVolumeRate(0);
      }
    }, [selectedRelaxedRules]);

    // localStorage 復元
    useEffect(() => {
      const json = localStorage.getItem('PlanningPresenterSettings');
      if (!json) return;

      const saved = JSON.parse(json) as Partial<PlanningConditions & {
        priorityMode?: 'none' | 'nearby' | 'far';
        vehicleSizePreference?: VehicleSizePreference;
        dispatchModel?: DispatchModel;
      }>;

      if (saved.howToPlanning === '最適化' || saved.howToPlanning === '平準化') {
        setHowToPlanning(saved.howToPlanning);
      }
      if (saved.availabilityOfExpressway !== undefined) setAvailabilityOfExpressway(saved.availabilityOfExpressway);
      if (saved.includeFutureDeliveries !== undefined) setIncludeFutureDeliveries(saved.includeFutureDeliveries);
      // Why not splitOrders も復元する: 案件分割は配車結果を実際に変えるので、
      // 前回 ON にしたことを忘れたまま次の配車が走らないよう毎回 false から始める。
      // V2Plans も同じ localStorage キーを読みながら setAutoSplitOrders(false) で固定している
      // (V2Plans/presenters/PlanningPresenter.tsx:420)。保存はする（履歴に何を選んで実行したかは残す）
      if (saved.dontExchangePreroute !== undefined) setDontExchangePreroute(saved.dontExchangePreroute);
      if (saved.concurrentAllOrNothing !== undefined) setConcurrentAllOrNothing(saved.concurrentAllOrNothing);
      if (saved.activateIncrementalMaxNumPistons !== undefined) setActivateIncrementalMaxNumPistons(saved.activateIncrementalMaxNumPistons);
      if (saved.selectedRelaxedRules) setSelectedRelaxedRules(saved.selectedRelaxedRules);
      if (saved.expandWorkingTimeStartMinutes !== undefined) setExpandWorkingTimeStartMinutes(saved.expandWorkingTimeStartMinutes);
      if (saved.expandWorkingTimeEndMinutes !== undefined) setExpandWorkingTimeEndMinutes(saved.expandWorkingTimeEndMinutes);
      if (saved.expandLoadStartMinutes !== undefined) setExpandLoadStartMinutes(saved.expandLoadStartMinutes);
      if (saved.expandLoadEndMinutes !== undefined) setExpandLoadEndMinutes(saved.expandLoadEndMinutes);
      if (saved.expandUnloadStartMinutes !== undefined) setExpandUnloadStartMinutes(saved.expandUnloadStartMinutes);
      if (saved.expandUnloadEndMinutes !== undefined) setExpandUnloadEndMinutes(saved.expandUnloadEndMinutes);
      if (saved.expandMaxVolumeRate !== undefined) setExpandMaxVolumeRate(saved.expandMaxVolumeRate);
      if (saved.expandMaxLoadCapacityRate !== undefined) setExpandMaxLoadCapacityRate(saved.expandMaxLoadCapacityRate);
      // Why not 新キー(planningPriority)だけを見る: buildPlanningConditionsSnapshot 導入後は
      //   planningPriority (日本語ラベル) で保存されるが、導入前に保存された旧キー(priorityMode)
      //   のデータがユーザーのブラウザに残っている間はそちらも復元できないと設定が消えて見える。
      if (saved.planningPriority) {
        setPriorityMode(planningPriorityToPriorityMode(saved.planningPriority));
      } else if (saved.priorityMode) {
        setPriorityMode(saved.priorityMode);
      }
      if (saved.vehicleSizePreference) setVehicleSizePreference(saved.vehicleSizePreference);
      if (saved.dispatchModel) setDispatchModel(saved.dispatchModel);
    }, [licenseContext.config, setSelectedRelaxedRules]);

    // localStorage 保存 + 履歴 snapshot (onPlanningConditionsChange) への通知
    // How: 両者が別々に object を手組みすると食い違うため、buildPlanningConditionsSnapshot の
    //      返り値1つだけを両方の送り先に渡す（RED 5: 同じ snapshot を受け取る）。
    useEffect(() => {
      const snapshot = buildPlanningConditionsSnapshot({
        howToPlanning,
        priorityMode,
        vehicleSizePreference,
        dispatchModel,
        availabilityOfExpressway,
        includeFutureDeliveries,
        autoSplitOrders,
        dontExchangePreroute,
        concurrentAllOrNothing,
        activateIncrementalMaxNumPistons,
        selectedRelaxedRules,
        expandWorkingTimeStartMinutes,
        expandWorkingTimeEndMinutes,
        expandLoadStartMinutes,
        expandLoadEndMinutes,
        expandUnloadStartMinutes,
        expandUnloadEndMinutes,
        expandMaxVolumeRate,
        expandMaxLoadCapacityRate,
      });
      localStorage.setItem('PlanningPresenterSettings', JSON.stringify(snapshot));
      onPlanningConditionsChange?.(snapshot);
    }, [
      howToPlanning,
      priorityMode,
      vehicleSizePreference,
      availabilityOfExpressway,
      includeFutureDeliveries,
      autoSplitOrders,
      dontExchangePreroute,
      concurrentAllOrNothing,
      activateIncrementalMaxNumPistons,
      selectedRelaxedRules,
      expandWorkingTimeStartMinutes,
      expandWorkingTimeEndMinutes,
      expandLoadStartMinutes,
      expandLoadEndMinutes,
      expandUnloadStartMinutes,
      expandUnloadEndMinutes,
      expandMaxVolumeRate,
      expandMaxLoadCapacityRate,
      dispatchModel,
      onPlanningConditionsChange,
    ]);

    const onClick = () => {
      if (isExecuting) return;
      setIsExecuting(true);
      ReactGA.event('executePlanning', { screen_name: screenName, button_name: '自動配車' });
      onClose();
      planningRequest(selectedDeliveryIds, selectedOrderIds);
    };

    const handleChipToggle = (rule: RelaxedRuleVo) => {
      if (selectedRelaxedRules.includes(rule)) {
        setSelectedRelaxedRules(selectedRelaxedRules.filter((r) => r !== rule));
      } else {
        setSelectedRelaxedRules([...selectedRelaxedRules, rule]);
      }
    };

    return (
      <>
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <TuneRoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold" lineHeight={1.3}>
              自動配車の設定
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            配車条件を設定して自動割り当てを実行します
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: 'auto',
            maxHeight: '80vh',
            px: 3,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {(hiddenSelectedOrderCount ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              選択中のうち {hiddenSelectedOrderCount} 件は現在のフィルタで非表示です。非表示の案件も配車対象に含まれます。
            </Alert>
          )}

          {/* Section 1: 計算方法 */}
          <Box>
            <SectionLabel>計算方法</SectionLabel>
            <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
              <Select
                value={howToPlanning}
                onChange={(e: SelectChangeEvent) => setHowToPlanning(e.target.value as HowToPlanning)}
              >
                <MenuItem value="最適化">最適化 — トラック台数と時間距離を最小化</MenuItem>
                <MenuItem value="平準化">平準化 — 割り付ける荷物を均等化</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Section 2: 優先設定 */}
          <Box>
            <SectionLabel>優先設定</SectionLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  エリア優先
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={priorityMode}
                  onChange={(_, v: 'none' | 'nearby' | 'far' | null) => {
                    if (v !== null) setPriorityMode(v);
                  }}
                >
                  {([
                    { value: 'none', label: '指定なし' },
                    { value: 'nearby', label: '近隣' },
                    { value: 'far', label: '遠方' },
                  ] as const).map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  車両サイズ優先
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={vehicleSizePreference}
                  onChange={(_, v: VehicleSizePreference | null) => {
                    if (v !== null) setVehicleSizePreference(v);
                  }}
                >
                  {([
                    { value: 'none', label: '指定なし' },
                    { value: 'small', label: '小型' },
                    { value: 'large', label: '大型' },
                  ] as const).map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <ToggleSwitchRow
                  label="有料道路使用"
                  checked={availabilityOfExpressway}
                  onChange={setAvailabilityOfExpressway}
                />
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <ToggleSwitchRow
                  label="宵積み有効"
                  checked={includeFutureDeliveries}
                  onChange={setIncludeFutureDeliveries}
                />
              </Box>
            </Box>
          </Box>

          {/* Section 3: 配車方式 */}
          <Box>
            <SectionLabel>配車方式</SectionLabel>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {/* ToggleSwitchRow × 2: 2カラムグリッド */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <ToggleSwitchRow
                  label="既存割り当てを維持"
                  tooltip="新しいオーダーを追加配車する際、既存のドライバー割り当てを維持するか、ドライバー間で割り当てを変更するかを選択できます。"
                  checked={dontExchangePreroute}
                  onChange={setDontExchangePreroute}
                />
                <ToggleSwitchRow
                  label="回転数を平準化"
                  tooltip="各車両の回転数をなるべく均等に配分します"
                  checked={activateIncrementalMaxNumPistons}
                  onChange={setActivateIncrementalMaxNumPistons}
                />
              </Box>
              {/* Checkbox × 2: 縦並び・左インデント */}
              <Stack spacing={0} sx={{ pl: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={autoSplitOrders}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setAutoSplitOrders(e.target.checked)}
                      sx={{ py: 0.25 }}
                    />
                  }
                  label={<Typography variant="body2">積載量超過時に案件を自動分割</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={concurrentAllOrNothing}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setConcurrentAllOrNothing(e.target.checked)}
                      sx={{ py: 0.25 }}
                    />
                  }
                  label={<Typography variant="body2">同一ロケーションで複数台になる場合は割り当てない</Typography>}
                />
              </Stack>
            </Stack>
          </Box>

          {/* Section 4: モデル選択 (use_ai_planning=trueのときのみ) */}
          {useAiPlanning && (
            <Box>
              <SectionLabel>モデル</SectionLabel>
              <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
                <Select
                  value={dispatchModel}
                  onChange={(e: SelectChangeEvent) => setDispatchModel(e.target.value as DispatchModel)}
                  renderValue={(value) => {
                    const model = dispatchModelDefs.find((m) => m.value === value);
                    // How: 選択値のラベルは他の Select(計算方法)と同じ既定サイズ(body1)に揃える。
                    //      説明は body2 グレーで従属表示。ドロップダウン内 MenuItem は body2/caption のまま。
                    return (
                      <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{model?.label}</Typography>
                        <Typography variant="body2" color="text.secondary">— {model?.description}</Typography>
                      </Stack>
                    );
                  }}
                >
                  {dispatchModelDefs.map((model) => (
                    <MenuItem key={model.value} value={model.value}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{model.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{model.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Section 5: 制約 (Accordion) */}
          <Accordion
            disableGutters
            elevation={0}
            expanded={constraintExpanded}
            onChange={(_, expanded) => setConstraintExpanded(expanded)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <SectionLabel>制約</SectionLabel>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* 無効にする制約 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    無効にする制約
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', minWidth: 0, p: 0.5 }}
                      onClick={() => setSelectedRelaxedRules([...relaxedRules])}
                    >
                      全選択
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', minWidth: 0, p: 0.5 }}
                      onClick={() => setSelectedRelaxedRules([])}
                    >
                      全解除
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {relaxedRules.map((rule) => {
                    const isSelected = selectedRelaxedRules.includes(rule);
                    return (
                      <Chip
                        key={rule}
                        label={rule}
                        size="small"
                        onClick={() => handleChipToggle(rule)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* 制約緩和 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    制約緩和
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ fontSize: '0.65rem', minWidth: 0, p: '2px 6px', lineHeight: 1 }}
                    onClick={() => {
                      setExpandWorkingTimeStartMinutes(0);
                      setExpandWorkingTimeEndMinutes(0);
                      setExpandLoadStartMinutes(0);
                      setExpandLoadEndMinutes(0);
                      setExpandUnloadStartMinutes(0);
                      setExpandUnloadEndMinutes(0);
                      setExpandMaxLoadCapacityRate(0);
                      setExpandMaxVolumeRate(0);
                    }}
                  >
                    リセット
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="勤務開始"
                      value={expandWorkingTimeStartMinutes}
                      onChange={setExpandWorkingTimeStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('労働時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="勤務終了"
                      value={expandWorkingTimeEndMinutes}
                      onChange={setExpandWorkingTimeEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('労働時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="集荷開始"
                      value={expandLoadStartMinutes}
                      onChange={setExpandLoadStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="集荷締切"
                      value={expandLoadEndMinutes}
                      onChange={setExpandLoadEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="納品開始"
                      value={expandUnloadStartMinutes}
                      onChange={setExpandUnloadStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="納品締切"
                      value={expandUnloadEndMinutes}
                      onChange={setExpandUnloadEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="積載重量"
                      value={expandMaxLoadCapacityRate}
                      onChange={setExpandMaxLoadCapacityRate}
                      unit="%"
                      max={100}
                      disabled={selectedRelaxedRules.includes('重量')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="積載体積"
                      value={expandMaxVolumeRate}
                      onChange={setExpandMaxVolumeRate}
                      unit="%"
                      max={100}
                      disabled={selectedRelaxedRules.includes('体積')}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button variant="text" size="small" onClick={onClose} color="inherit">
            キャンセル
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<LocalShippingRoundedIcon />}
            onClick={onClick}
            disabled={isExecuting}
          >
            実行する
          </Button>
        </Box>
      </>
    );
  }
);

export default PlanningPresenter;
